import {IEventsDomainStore} from "../../domainStore/pages/eventsDomainStore";
import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction
} from "mobx";
import {IModalStore} from "../global/modalStore";
import {debounce, Dictionary} from "lodash";
import {ObservableList} from "../../observables/observableList";
import {KeyValue} from "../../observables/keyValue";
import CalendarSchedule from "../../domainStore/pages/models/calendarSchedule";
import EventBooking from "../../domainStore/pages/models/eventBooking";
import {SortOrder} from "../../../shared/enums/sortOrder";
import {IResidentAdminDomainStore} from "../../domainStore/pages/residentAdminDomainStore";
import ResidentDetail from "../../domainStore/pages/models/residentDetail";
import {BookingColumnNames} from "../../../shared/enums/eventBookingColumnNames";
import {
  calendarViewCategory,
  calendarViewMonth,
  independentResidents,
  multiVillageKeyValue
} from "../../../shared/constants";
import {EventBookingStatus} from "../../../shared/enums/eventBookingStatus";
import {ProblemDetails} from "../../../shared/interfaces/responses/problemDetails";
import {ErrorCodes} from "../../../shared/enums/errorCodes";
import {EventBookingSource} from "../../../shared/enums/eventBookingSource";
import {isDateOneBeforeDateTwo} from "../../../utils/dateUtils";
import ResidentBookingGroup from "../../domainStore/pages/models/residentBookingGroup";
import ResidentData from "../../domainStore/pages/models/residentData";
import BookingNotes from "../../domainStore/pages/models/bookingNotes";
import {EventBookingErrorCodes} from "../../../shared/enums/eventBookingErrorCodes";
import {addDays} from "date-fns";
import {EventCalendarType} from "../../../shared/enums/eventCalendarType";
import {
  getEventCalendarTypeDisplay,
  hasEnhancedFeatures,
  toEventCalendarType
} from "../../../shared/eventCalendarTypeHelper";
import {Color} from "../../../shared/enums/color";
import {IconSource} from "../../../shared/enums/iconSource";
import {zonedTimeToUtc} from "date-fns-tz";
import {AccordionRecords} from "../../../shared/interfaces/accordionRecords";
import {DepartmentTypeCode} from "../../../shared/enums/departmentTypeCode";
import {
  isIndependentPersonType,
  isServicedPersonType
} from "../../../shared/personTypeIds";
import {BookingsSortUtil} from "../../../utils/sortUtils/bookingsSortUtil";
import {compareNumbers} from "../../../utils/sortUtils/sortUtil";
import {IVillageCalendarsDomainStore} from "../../domainStore/pages/villageCalendarsDomainStore";

export type BookingUpdateError =
  | "createBookingNotEnoughCapacity"
  | "createBookingFailed"
  | "deleteBookingFailed"
  | "editBookingFailed"
  | "createBookingConfilictingRequest"
  | "createBookingAlreadyExists";

// Ignoring this unfortunately because eventsUiStore calls backendcaller before we can mock it
/* istanbul ignore file */

export interface IEventsUiStore {
  /**
   * Flag that denotes navigation between calendar dates
   */
  isNavigating: boolean;
  /**
   * Flag that denotes loading of data.
   */
  isLoading: boolean;
  /**
   * Flag that denotes if event is in creating or editing mode.
   */
  isCreatingOrEditing: boolean;

  /**
   * Flag that denotes if user is viewing event in read only view.
   */
  isViewingEvent: boolean;

  /**
   * Denotes the initial calendar date that shows.
   */
  initialCalendarDate: Date;
  /**
   * Method used to navigate between calendar dates.
   */
  navigateCalendarByDates: (startDate: Date, endDate: Date) => Promise<void>;
  /**
   * Method used to when one wants to change village.
   */
  onSelectedVillageChange: () => Promise<void>;
  /**
   * List of Villages
   */
  selectableVillages?: ObservableList<KeyValue>;
  /**
   * Calendar schedule
   */
  calendarSchedule?: CalendarSchedule;
  /**
   * Selected calendar view
   */
  currentCalendarView: string;
  /**
   * Method to reset the initial calendar date
   */
  resetInitialCalendarDate: () => void;
  /**
   * Method to reset isCreatingOrEditing flag
   */
  resetIsCreatingOrEditingFlag: () => void;

  /**
   * Method to reset IsViewingEventFlag flag
   */
  resetIsViewingEventFlag: () => void;
  /**
   * Denotes loading of event bookings
   */
  isLoadingEventBookings: boolean;
  /**
   * Search term for when searching resident to add resident into bookings.
   */
  residentSearchTerm: string | undefined;
  /**
   * The set of confirmed bookings for an event.
   * This is used as a temporary storage for the bookings to reflect the immediate UI changes.
   */
  confirmedEventBookings: EventBooking[];
  /**
   * The set of waitlisted bookings for an event.
   * This is used as a temporary storage for the bookings to reflect the immediate UI changes.
   */
  waitlistEventBookings: EventBooking[];
  /**
   * The set of cancelled bookings for an event.
   * This is used as a temporary storage for the bookings to reflect the immediate UI changes.
   */
  cancelledEventBookings: EventBooking[];
  /**
   * The current column we are sorting on
   */
  bookingsSortField: BookingColumnNames;
  /**
   * The current sort order
   */
  bookingsSortOrder: SortOrder;
  /**
   * The default calendar view
   */
  readonly defaultCalendarView: string;
  /**
   * Load the bookings for an event, while resetting all data
   */
  loadEventBookings: (eventId: string) => Promise<void>;
  /**
   * Reload the bookings for an event, without resetting other data
   */
  reloadEventBookings: () => Promise<void>;
  /**
   * Sort the event bookings
   */
  sortEventBookings: (
    sortField: BookingColumnNames,
    sortOrder: SortOrder
  ) => void;
  /**
   * Updates an event booking.
   * @param eventBooking The event booking being updated
   * @param immediate Should the update be saved immediately. Defaults to false
   */
  updateEventBooking: (eventBooking: EventBooking, immediate?: boolean) => void;
  /**
   * Add a resident booking to the event
   * @param residentGroup The selected resident group
   * @param status The new state to add for the bookings
   * @param index The index for new bookings
   * @param isPaid The paid flag for new bookings
   */
  addResidentBooking: (
    residentGroup: ResidentBookingGroup,
    status: EventBookingStatus,
    index: number,
    isPaid: boolean | undefined
  ) => Promise<void>;
  /**
   * Reload an event's details
   */
  reloadEventDetails: (eventId: string) => Promise<void>;
  /**
   * Reset to default values
   */
  resetEventBookings: () => void;
  /**
   * Load a list of residents based on the search term.
   */
  searchResidents: () => Promise<number>;
  /**
   * Get a list of residents.
   */
  getResidents: () => ResidentDetail[];
  /**
   * Reset residents list to it's default state
   */
  resetResidents: () => void;
  /**
   * Reload full event details and related bookings.
   */
  reloadEventDetailsAndBookings: (
    id?: string,
    villageId?: string
  ) => Promise<void>;
  /**
   * A complete set of bookings, with all pending edits applied
   */
  allBookings: EventBooking[];
  /**
   * A record of selected booking IDs.
   */
  selectedBookingIds: Set<string>;
  /**
   * gets a flag indicating whether there are unsaved booking changes
   */
  hasUnsavedBookings: boolean;
  /**
   * clears all the currently selected bookings
   */
  clearSelectedBookings: () => void;
  /**
   * Clears a specific booking
   */
  clearSelectedBooking: (eventBooking: EventBooking) => void;
  /**
   * Did the user use the multi select tools to confirm muliple bookings.
   */
  isConfirmingBookings: boolean;
  /**
   * Did the user use the multi select tools to delete multiple bookings.
   */
  isDeletingBookings: boolean;
  /**
   * Returns all bookings which are part of a booking group.
   * @param eventBookings list of event bookings to search
   * @param creatorId The id of the person who created the booking
   * @returns plus one booking pair
   */
  getBookingGroup: (
    creatorId: string,
    eventBookings?: EventBooking[]
  ) => EventBooking[];
  /**
   * Toggle the selected booking in the record.
   */
  toggleSelectedBooking: (eventBooking: EventBooking) => void;
  /**
   * Update all selected bookings.
   */
  updateSelectedBookings: (status: EventBookingStatus) => Promise<void>;
  /**
   * Are any of the selected bookings marked as paid
   */
  selectedBookingsIncludesPaidBooking: boolean;
  /**
   * Checks if the currently selected event is released.
   */
  isCurrentEventReleased: () => boolean;

  /**
   * Groups an array of event bookings by the creator id
   * @param bookings The array of event bookings to group
   */
  groupByCreatorId(bookings: EventBooking[]): Dictionary<EventBooking[]>;

  /**
   * Get the time zone of the selected village.
   */
  getTimeZone: () => string;

  /**
   * The state of any errors that occurred when editing bookings for an event
   */
  updateBookingError?: BookingUpdateError;
  /**
   * Denotes start date for the calendar view.
   * Example: calendarStart for `month view` for June 2023 will be 29th May
   */
  calendarStart?: Date;
  /**
   * Denotes end date for the calendar view.
   * Example: calendarEnd for `month view` for June 2023 will be 2nd July
   */
  calendarEnd?: Date;

  /**
   * Checks if event series is being edited.
   */
  isEditingEventSeries: boolean;

  /**
   * Setter for isEditingEventSeries flag
   */
  setEditingEventSeries: (editEventSeries?: boolean) => void;

  /**
   * Checks if event series is being viewed.
   */
  isViewingEventSeries: () => boolean;

  /**
   * Controls publish disabled / enabled state
   */
  canPublish: () => boolean;

  /**
   * Selected event calendar type.
   */
  selectedCalendarType: EventCalendarType;

  /**
   * Load categories in the selected event calendar type.
   */
  loadEventCategories: () => Promise<void>;

  /**
   * Get color of the selected calendar.
   */
  calendarColor?: Color;

  /**
   * The name of the selected calendar.
   */
  selectedCalendarName: string;

  /**
   * Get icon of the selected calendar.
   */
  calendarIcon: IconSource;

  /**
   * True if the selected calendar supports the full (Enhanced) feature set,
   * i.e. it is an Enhanced or Wellbeing calendar.
   */
  isEnhancedCalendar: boolean;

  /**
   * Get calendar type by selected calendar name.
   */
  calendarTypeBySelectedCalendarName: EventCalendarType;

  /**
   * Get person type Id by selected calendar name.
   *
   * @deprecated Prefer {@link personTypeIdsBySelectedCalendarName} which
   * returns the full CMS-first multi list. This single-value getter is
   * kept for legacy callers (e.g. the categorised-events fetch that only
   * supports a single personType filter).
   */
  personTypeIdBySelectedCalendarName?: string;

  /**
   * Resolves the full set of personTypeIds that the selected village
   * calendar covers. The order of resolution is:
   *
   * 1. The CMS Event API calendar (surfaced via
   *    {@link IVillageCalendarsDomainStore.villageCalendars}, which the
   *    admin BFF `/api/villagecalendars` endpoint fetches from
   *    `internal/event/v1/calendars`). Matched by calendar name +
   *    villageId, filtered to that village's personTypeIds. If a non-empty
   *    list is available it is returned as-is.
   * 2. Otherwise, `personTypeIds` declared directly on the calendar object
   *    itself (e.g. the synthetic Multi-Village calendars, which have no
   *    real villageId to match against the CMS store above).
   * 3. Otherwise fall back to the Integration API's `VillageCalendar`
   *    (from `internal/integration/v1/villages`) wrapped as a
   *    single-element array.
   *
   * Returns an empty array when none of the sources has a value.
   */
  personTypeIdsBySelectedCalendarName: string[];

  /**
   * True if the user is switching to another village.
   */
  isSwitchingVillage: boolean;

  /**
   * Loads the calendar release schedule for the current village.
   */
  loadEventReleaseDate: () => Promise<void>;

  /**
   * Load categorised events.
   */
  loadCategorisedEvents: (startDate: Date, endDate: Date) => Promise<void>;

  /**
   * Records of the expandable state of accordions.
   */
  accordionRecords: AccordionRecords;

  /**
   * Stores which occurrence date was previously selected for recurring events.
   */
  selectedOccurrenceDate?: Date | null;

  /**
   * Sets which occurrence date was originally selected before moving to the edit this series page.
   */
  setSelectedOccurrenceDate: (occurrenceDate: Date | null) => void;
}

export class EventsUiStore implements IEventsUiStore {
  private eventsDomainStore: IEventsDomainStore;
  private residentDomainStore: IResidentAdminDomainStore;
  private modalStore: IModalStore;
  private villageCalendarsDomainStore?: IVillageCalendarsDomainStore;
  selectedCalendarType = EventCalendarType.Enhanced;
  selectedCalendarName = "";
  calendarStart?: Date;
  calendarEnd?: Date;
  initialCalendarDate: Date;
  isNavigating: boolean;
  isLoading: boolean;
  isCreatingOrEditing: boolean = false;
  isViewingEvent: boolean = false;
  selectableVillages: ObservableList<KeyValue>;
  calendarSchedule?: CalendarSchedule;
  currentCalendarView: string;
  isLoadingEventBookings: boolean;
  residentSearchTerm: string | undefined;

  confirmedEventBookings: EventBooking[];
  waitlistEventBookings: EventBooking[];
  bookingsSortField: BookingColumnNames;
  bookingsSortOrder: SortOrder;
  cancelledEventBookings: EventBooking[];
  selectedBookingIds: Set<string>;
  isConfirmingBookings: boolean = false;
  isDeletingBookings: boolean = false;
  isEditingEventSeries = false;
  isSwitchingVillage = false;
  updateBookingError?: BookingUpdateError = undefined;
  accordionRecords: AccordionRecords = {};
  selectedOccurrenceDate?: Date | null;

  // A working list of updates which are yet to be saved
  pendingBookingUpdates: Set<string> = new Set<string>();

  readonly defaultCalendarView: string = calendarViewMonth;

  // The time in milliseconds we wait between queuing a change, and processing that change
  private pendingBookingsDebounce = 2000;
  private calendarRequestId = 0;
  private villageChangeRequestId = 0;
  private releaseDateRequestId = 0;

  get hasUnsavedBookings() {
    return this.pendingBookingUpdates.size > 0;
  }

  get allBookings() {
    return [
      ...this.confirmedEventBookings,
      ...this.waitlistEventBookings,
      ...this.cancelledEventBookings
    ];
  }

  get selectedBookingsIncludesPaidBooking() {
    return this.allBookings.some(
      (b) => b.isPaid && this.selectedBookingIds.has(b.id)
    );
  }

  constructor(
    eventsDomainStore: IEventsDomainStore,
    modalStore: IModalStore,
    selectableVillages: ObservableList<KeyValue>,
    residentDomainStore: IResidentAdminDomainStore,
    villageCalendarsDomainStore?: IVillageCalendarsDomainStore
  ) {
    makeObservable(this, {
      isNavigating: observable,
      isLoading: observable,
      isCreatingOrEditing: observable,
      isViewingEvent: observable,
      initialCalendarDate: observable,
      isLoadingEventBookings: observable,
      currentCalendarView: observable,
      navigateCalendarByDates: action,
      onSelectedVillageChange: action,
      selectableVillages: observable,
      calendarSchedule: observable,
      resetInitialCalendarDate: action,
      resetIsCreatingOrEditingFlag: action,
      resetIsViewingEventFlag: action,
      residentSearchTerm: observable,
      confirmedEventBookings: observable,
      waitlistEventBookings: observable,
      cancelledEventBookings: observable,
      bookingsSortField: observable,
      bookingsSortOrder: observable,
      loadEventBookings: action,
      reloadEventBookings: action,
      sortEventBookings: action,
      updateEventBooking: action,
      addResidentBooking: action,
      updateSelectedBookings: action,
      reloadEventDetails: action,
      resetEventBookings: action,
      reloadEventDetailsAndBookings: action,
      selectedBookingIds: observable,
      isConfirmingBookings: observable,
      isDeletingBookings: observable,
      clearSelectedBookings: action,
      clearSelectedBooking: action,
      toggleSelectedBooking: action,
      pendingBookingUpdates: observable,
      updateBookingError: observable,
      allBookings: computed,
      hasUnsavedBookings: computed,
      selectedBookingsIncludesPaidBooking: computed,
      selectedCalendarType: observable,
      selectedCalendarName: observable,
      isSwitchingVillage: observable,
      accordionRecords: observable
    });

    this.eventsDomainStore = eventsDomainStore;
    this.residentDomainStore = residentDomainStore;
    this.modalStore = modalStore;
    this.selectableVillages = selectableVillages;
    this.villageCalendarsDomainStore = villageCalendarsDomainStore;
    this.currentCalendarView = this.defaultCalendarView;

    this.isNavigating = false;
    this.isLoading = false;
    this.initialCalendarDate = new Date();
    this.calendarStart = undefined;
    this.calendarEnd = undefined;
    this.isLoadingEventBookings = false;
    this.residentSearchTerm = "";
    this.modalStore = modalStore;
    this.isLoading = false;
    this.confirmedEventBookings = [];
    this.waitlistEventBookings = [];
    this.cancelledEventBookings = [];
    this.bookingsSortField = BookingColumnNames.Index;
    this.bookingsSortOrder = SortOrder.Ascending;
    this.selectedBookingIds = new Set<string>();
    this.isEditingEventSeries = false;
    this.accordionRecords = {calendarViewCategory: {}};

    reaction(
      () => this.eventsDomainStore.categorisedEvents,
      () => {
        this.accordionRecords[calendarViewCategory] =
          this.eventsDomainStore.categorisedEvents.reduce(
            (acc, cur) => {
              acc = {...acc, [cur.id]: false};
              return acc;
            },
            {} as {[accordionId: string]: boolean}
          );
      }
    );
  }

  // Queue the processPendingBookings method to happen after a period of inactivity
  private processPendingBookingsDebounce = debounce(async () => {
    await this.processPendingBookings();
  }, this.pendingBookingsDebounce);

  /**
   * Process all pending bookings changes
   * @param resetOnFinish Should this function trigger the reset process when complete.
   * @returns Was the update successful
   */
  private processPendingBookings = async (
    resetOnFinish: boolean = true
  ): Promise<boolean> => {
    runInAction(() => {
      this.isLoadingEventBookings = true;
    });

    const updatedBookings = this.allBookings.filter((b) =>
      this.pendingBookingUpdates.has(b.id)
    );

    try {
      await this.eventsDomainStore.updateMultipleEventBookings(updatedBookings);
    } catch (e: any) {
      const errorMessage = this.getErrorMessage(e, "editBookingFailed");
      runInAction(() => {
        this.updateBookingError = errorMessage;
      });
      return false;
    }

    this.pendingBookingUpdates.clear();
    if (resetOnFinish) {
      await this.reloadEventDetailsAndBookings();
    }

    return true;
  };

  setEditingEventSeries = (editEventSeries?: boolean) => {
    this.isEditingEventSeries = editEventSeries ?? false;
  };

  resetInitialCalendarDate() {
    runInAction(() => {
      this.initialCalendarDate = new Date();
    });
    this.calendarStart = new Date();
    this.calendarEnd = new Date();
  }

  resetIsCreatingOrEditingFlag = () => {
    runInAction(() => (this.isCreatingOrEditing = false));
  };

  resetIsViewingEventFlag = () => {
    runInAction(() => (this.isViewingEvent = false));
  };

  onSelectedVillageChange = async () => {
    const villageRequestId = ++this.villageChangeRequestId;
    const requestId = ++this.calendarRequestId;
    ++this.releaseDateRequestId;
    this.eventsDomainStore.invalidateCalendarRequests();
    runInAction(() => {
      // A superseded navigation won't update initialCalendarDate, and the calendar remounts with it.
      if (this.isNavigating && this.calendarStart) {
        this.syncInitialCalendarDate();
      }
      this.isSwitchingVillage = true;
    });
    if (this.calendarStart && this.calendarEnd) {
      try {
        await this.loadCalendarEventsForCurrentView(
          this.calendarStart,
          this.calendarEnd
        );
      } catch (error) {
        if (requestId === this.calendarRequestId) {
          this.modalStore.openErrorModal(error as Error);
        }
      }
      if (requestId === this.calendarRequestId) {
        await this.loadEventReleaseDate();
      }
    }
    if (requestId === this.calendarRequestId) {
      runInAction(() => {
        this.isLoading = false;
        this.isNavigating = false;
      });
    }
    if (villageRequestId === this.villageChangeRequestId) {
      runInAction(() => {
        this.isSwitchingVillage = false;
      });
    }
  };

  navigateCalendarByDates = async (startDate: Date, endDate: Date) => {
    const requestId = ++this.calendarRequestId;
    this.eventsDomainStore.invalidateCalendarRequests();
    runInAction(() => {
      this.isNavigating = true;
    });
    let nextStart = startDate;
    let nextEnd = endDate;
    //Delay loading of data in case the user rapidly presses the next or previous buttons
    //may remove once we add datepicker
    // A new debouncer is created per call, so the requestId check is what drops superseded navigations.
    debounce(async () => {
      if (requestId !== this.calendarRequestId) {
        return;
      }
      if (nextStart === this.calendarStart && nextEnd === this.calendarEnd) {
        runInAction(() => {
          this.syncInitialCalendarDate();
          this.isLoading = true;
        });
        try {
          await this.loadCalendarEventsForCurrentView(
            this.calendarStart,
            this.calendarEnd
          );

          // get calendar schedule when selecting non multi village or when no village is selected
          if (requestId === this.calendarRequestId) {
            await this.loadEventReleaseDate();
          }
        } catch (error) {
          if (requestId === this.calendarRequestId) {
            this.modalStore.openErrorModal(error as Error);
          }
        }
      }
      if (requestId === this.calendarRequestId) {
        runInAction(() => {
          this.isLoading = false;
          this.isNavigating = false;
        });
      }
    }, 300)();
    runInAction(() => {
      this.calendarStart = startDate;
      this.calendarEnd = endDate;
    });
  };

  private syncInitialCalendarDate() {
    this.initialCalendarDate =
      this.currentCalendarView === calendarViewMonth
        ? addDays(this.calendarStart || new Date(), 7)
        : this.calendarStart || new Date();
  }

  updateEventBooking(eventBooking: EventBooking, immediate: boolean = false) {
    runInAction(() => {
      this.pendingBookingUpdates.add(eventBooking.id);
      eventBooking.isStatusSpinning = true;
    });

    this.processPendingBookingsDebounce();

    if (immediate) {
      this.processPendingBookingsDebounce.flush();
    }
  }

  async addResidentBooking(
    bookingGroup: ResidentBookingGroup,
    status: EventBookingStatus,
    index: number,
    isPaid: boolean | undefined
  ) {
    if (status === EventBookingStatus.Cancelled) {
      // User can not "add" a cancelled booking, only update exiting bookings
      return;
    }

    const isPendingBookingsClear = await this.clearPendingChanges();
    if (!isPendingBookingsClear) {
      return;
    }

    runInAction(() => {
      this.isLoadingEventBookings = true;
    });

    try {
      // Check if we are updating existing bookings, or creating new bookings
      if (bookingGroup.bookingStatus) {
        const bookingsToUpdate = this.allBookings.filter(
          (b) => b.personId === bookingGroup.mainResidentPlatformId
        );

        bookingsToUpdate.forEach((b, i) => {
          // We show the booking in its original spot, and it's new spot
          // Create a mirror of this booking in the new table
          const generatedBooking = this.generateEventBooking(
            b.personId,
            b.creatorId,
            status,
            b.residentData.name,
            b.residentData.unitCode,
            b.residentData.phoneNumber,
            index + i,
            b.isPaid
          );

          runInAction(() => {
            switch (status) {
              case EventBookingStatus.Confirmed:
                this.confirmedEventBookings.push(generatedBooking);
                this.eventsDomainStore.editableEvent.bookingSpacesDetails.onBookingAdded(
                  b.source
                );
                break;
              case EventBookingStatus.Waitlist:
                this.waitlistEventBookings.push(generatedBooking);
                break;
            }

            generatedBooking.isSourceSpinning = true;
            generatedBooking.isStatusShimmer = true;

            b.isStatusSpinning = true;
            b.status = status;
          });
        });

        await this.eventsDomainStore.updateMultipleEventBookings(
          bookingsToUpdate
        );
      } else {
        const eventBooking = this.generateEventBooking(
          bookingGroup.mainResidentPlatformId,
          bookingGroup.mainResidentPlatformId,
          status,
          bookingGroup.mainResidentName,
          bookingGroup.unitCode,
          bookingGroup.mainResidentPhoneNumber,
          index,
          isPaid
        );

        eventBooking.isSourceSpinning = true;
        eventBooking.isStatusShimmer = true;

        runInAction(() => {
          switch (status) {
            case EventBookingStatus.Confirmed:
              this.confirmedEventBookings.push(eventBooking);
              this.eventsDomainStore.editableEvent.bookingSpacesDetails.onBookingAdded(
                EventBookingSource.Staff
              );
              break;
            case EventBookingStatus.Waitlist:
              this.waitlistEventBookings.push(eventBooking);
              break;
          }
        });

        const eventId = this.eventsDomainStore.editableEvent.id;

        await this.eventsDomainStore.createEventBooking(eventId, eventBooking);
      }
    } catch (e: any) {
      const errorMessage = this.getErrorMessage(e, "createBookingFailed");
      runInAction(() => {
        this.updateBookingError = errorMessage;
      });
    } finally {
      await this.reloadEventDetailsAndBookings();
    }
  }

  private generateEventBooking(
    residentId: string,
    creatorId: string,
    status: EventBookingStatus,
    residentName: string | undefined,
    unitCode: string | undefined,
    phoneNumber: string | undefined,
    index: number,
    isPaid: boolean | undefined
  ): EventBooking {
    return new EventBooking(
      `newBooking-${index}`,
      index,
      EventBookingSource.Staff,
      status,
      new ResidentData(residentName, phoneNumber, "", "", "", unitCode),
      new BookingNotes(),
      residentId,
      creatorId,
      isPaid
    );
  }

  reloadEventDetails = async (eventId: string, villageId?: string) => {
    await this.loadEventReleaseDate();
    await Promise.all([
      this.eventsDomainStore.getEventCategories(this.selectedCalendarType),
      this.eventsDomainStore.getEventLocations()
    ]);
    await this.eventsDomainStore.loadEditableEvent(eventId, false, villageId);
  };

  fetchBookingsFromDomainStore(eventId: string) {
    return this.eventsDomainStore.getEventBookings(eventId);
  }

  async loadEventBookings(eventId: string): Promise<void> {
    if (!eventId || !this.canLoadEventBookings()) {
      return;
    }

    runInAction(() => {
      this.resetEventBookings();
      this.isLoadingEventBookings = true;
    });

    await this.fetchBookingsFromDomainStore(eventId);

    runInAction(() => {
      this.loadBookingsFromDomainStore();
      this.isLoading = false;
      this.isLoadingEventBookings = false;
      this.isConfirmingBookings = false;
      this.isDeletingBookings = false;
      this.updateBookingError = undefined;
    });
  }

  private loadBookingsFromDomainStore = () => {
    // Use .slice to take a shallow copy of the bookings, maintaining the integrity of the domain store
    const confirmedBookings =
      this.eventsDomainStore.confirmedEventBookingsList?.slice() ?? [];
    const waitlistBookings =
      this.eventsDomainStore.waitlistEventBookingsList?.slice() ?? [];
    const cancelledBookings =
      this.eventsDomainStore.cancelledEventBookingsList?.slice() ?? [];
    this.confirmedEventBookings =
      this.sortBookingsBySortFields(confirmedBookings);
    this.waitlistEventBookings =
      this.sortBookingsByIndexAscending(waitlistBookings);
    this.cancelledEventBookings =
      this.sortBookingsByIndexAscending(cancelledBookings);
  };

  async reloadEventBookings(): Promise<void> {
    if (
      this.eventsDomainStore.editableEvent.id &&
      this.canLoadEventBookings()
    ) {
      runInAction(() => {
        this.isLoadingEventBookings = true;
        this.processPendingBookingsDebounce.cancel();
        this.pendingBookingUpdates.clear();
      });

      await this.fetchBookingsFromDomainStore(
        this.eventsDomainStore.editableEvent.id
      );

      runInAction(() => {
        this.loadBookingsFromDomainStore();
        // On a refresh, a booking may not be in the selected bookings list anymore, for example,
        // User 1 selects booking 1, 2, and 3
        // User 2 deletes booking 2
        // User 1 refreshes the bookings and only gets 1 and 3, but the selected bookings will still
        // contain booking 2. We need to remove all selected bookings that are not valid after a refresh
        const validSelectedBookings = Array.from(
          this.selectedBookingIds
        ).filter((x) => this.allBookings.some((b) => b.id === x));
        this.selectedBookingIds = new Set(validSelectedBookings);
        if (this.eventsDomainStore.editableEvent.isPassedEndDate) {
          this.selectedBookingIds.clear();
        }
        this.isLoadingEventBookings = false;
        this.isConfirmingBookings = false;
        this.isDeletingBookings = false;
        this.updateBookingError = undefined;
      });
    }
  }

  resetEventBookings() {
    runInAction(() => {
      this.isLoading = false;
      this.isLoadingEventBookings = false;
      this.confirmedEventBookings = [];
      this.waitlistEventBookings = [];
      this.cancelledEventBookings = [];
      this.selectedBookingIds.clear();
      this.pendingBookingUpdates.clear();
      this.processPendingBookingsDebounce.cancel();
      this.bookingsSortField = BookingColumnNames.Index;
      this.bookingsSortOrder = SortOrder.Ascending;
      this.residentSearchTerm = undefined;
      this.isConfirmingBookings = false;
      this.isDeletingBookings = false;
      this.updateBookingError = undefined;
    });
    // Also reset on domain store
    this.eventsDomainStore.resetEventBookings();
    this.residentDomainStore.resetResidents();
  }

  sortEventBookings(sortField: BookingColumnNames, sortOrder: SortOrder) {
    runInAction(() => {
      this.bookingsSortField = sortField;
      this.bookingsSortOrder = sortOrder;

      this.confirmedEventBookings = this.sortBookingsBySortFields(
        this.confirmedEventBookings
      );
    });
  }

  private sortBookingsBySortFields = (
    bookings: EventBooking[]
  ): EventBooking[] => {
    return new BookingsSortUtil(
      bookings,
      this.bookingsSortField,
      this.bookingsSortOrder
    ).sort();
  };

  private sortBookingsByIndexAscending = (
    bookings: EventBooking[]
  ): EventBooking[] => {
    return bookings.sort((a, b) => (a.index > b.index ? 1 : -1));
  };

  // Allows us to group app bookings together and ensure parent is sorted first,
  //   as +1 bookings does not participate in sorting when sorting by index.
  groupByCreatorId(bookings: EventBooking[]): Dictionary<EventBooking[]> {
    const bookingsByCreatorId: Dictionary<EventBooking[]> = {};
    bookings &&
      bookings.forEach((b) => {
        // Ensure we only group app bookings together, as it's common for 1 staff member to make multiple bookings.
        // For app bookings the key would be the creatorId, and for staff bookings key would be personId. This will
        //   ensure that staff bookings are not grouped at all.
        const groupingKey =
          b.source === EventBookingSource.App ? b.creatorId : b.personId;

        if (!bookingsByCreatorId[groupingKey]) {
          bookingsByCreatorId[groupingKey] = [];
        }
        bookingsByCreatorId[groupingKey].push(b);
        if (bookingsByCreatorId[groupingKey].length > 1) {
          const compareIndex = (a: EventBooking, b: EventBooking): number =>
            compareNumbers(a.index, b.index, this.bookingsSortOrder);

          const compareCreator = (a: EventBooking, b: EventBooking): number => {
            return a.creatorId === b.personId ? 1 : -1;
          };

          bookingsByCreatorId[groupingKey] = bookingsByCreatorId[
            groupingKey
          ].sort(
            bookingsByCreatorId[groupingKey][0].source ===
              EventBookingSource.App
              ? compareCreator
              : compareIndex
          );
        }
      });
    return bookingsByCreatorId;
  }

  searchResidents = (): Promise<number> => {
    return this.residentDomainStore.searchResidents(
      1,
      1000,
      undefined, //This is just a random field that's accepted by the backend.
      undefined,
      this.residentSearchTerm,
      this.bookingResidentDepartmentTypeCodes
    );
  };

  /**
   * Department codes matching the selected calendar's audience, used to scope
   * the booking resident search. Resolves the calendar's person types to
   * resident department codes (IA -> IND, SA -> SA); a unified calendar yields
   * both. Empty when the audience cannot be resolved, in which case the
   * resident store falls back to its configured default department.
   */
  private get bookingResidentDepartmentTypeCodes(): string[] {
    const codes = new Set<string>();
    for (const personTypeId of this.personTypeIdsBySelectedCalendarName) {
      if (isIndependentPersonType(personTypeId)) {
        codes.add(DepartmentTypeCode.Independent);
      } else if (isServicedPersonType(personTypeId)) {
        codes.add(DepartmentTypeCode.Serviced);
      }
    }
    return Array.from(codes);
  }

  getResidents = (): ResidentDetail[] => {
    return this.residentDomainStore.residents;
  };

  resetResidents = () => {
    this.residentDomainStore.resetResidents();
  };

  reloadEventDetailsAndBookings = async (id?: string, villageId?: string) => {
    const editableEventId = id || this.eventsDomainStore.editableEvent.id;
    if (!editableEventId) {
      return;
    }
    await this.reloadEventDetails(editableEventId, villageId);
    if (this.canLoadEventBookings()) {
      await this.reloadEventBookings();
    }
  };

  isSelectedVillageNotMultiVillage = (): boolean =>
    this.selectableVillages?.selectedItem?.value !== multiVillageKeyValue.value;

  loadEventReleaseDate = async () => {
    const requestId = ++this.releaseDateRequestId;
    try {
      if (
        this.selectableVillages.selectedItem?.value &&
        this.isSelectedVillageNotMultiVillage()
      ) {
        const newSchedule = await this.eventsDomainStore.getCalendarSchedule();
        if (requestId === this.releaseDateRequestId) {
          runInAction(() => {
            this.calendarSchedule = newSchedule;
          });
        }
      } else {
        runInAction(() => {
          this.calendarSchedule = undefined;
        });
      }
    } catch (error: any) {
      if (requestId !== this.releaseDateRequestId) {
        return;
      }
      // Catch error and do nothing if no event rollout can be found for the village.
      // for release 2.0.9 as we are still allowing a super-user
      // to select a village that does not support release schedule yet.
      // i.e. rollout rule hasn't been set for that village
      if (
        (error as ProblemDetails).detail !== ErrorCodes.EventRolloutNotFound
      ) {
        this.modalStore.openErrorModal(new Error());
      }

      runInAction(() => {
        this.calendarSchedule = undefined;
      });
    }
  };

  clearSelectedBookings = (): void => {
    runInAction(() => {
      this.selectedBookingIds.clear();
    });
  };

  clearSelectedBooking = (eventBooking: EventBooking): void => {
    runInAction(() => {
      this.selectedBookingIds.delete(eventBooking.id);
    });
  };

  getBookingGroup = (
    creatorId: string,
    eventBookings?: EventBooking[]
  ): EventBooking[] => {
    // We use this.allBookings to find bookings group when bookings in
    //   a pair have different statuses. Example, when one booking of the
    //   plus one group is in confirmed list and second booking is in waitlist.
    const bookings = eventBookings ? eventBookings : this.allBookings;
    return bookings.filter(
      (b) => b.creatorId === creatorId && b.source === EventBookingSource.App
    );
  };

  toggleSelectedBooking = (eventBooking: EventBooking): void => {
    this.selectedBookingIds.has(eventBooking.id)
      ? this.selectedBookingIds.delete(eventBooking.id)
      : this.selectedBookingIds.add(eventBooking.id);
  };

  updateSelectedBookings = async (
    targetStatus: EventBookingStatus
  ): Promise<void> => {
    const pendingChangesAreClear = await this.clearPendingChanges();
    if (!pendingChangesAreClear) {
      return;
    }

    runInAction(() => {
      this.isLoadingEventBookings = true;
      this.isConfirmingBookings = targetStatus === EventBookingStatus.Confirmed;
      this.isDeletingBookings = targetStatus === EventBookingStatus.Cancelled;

      this.allBookings.forEach((b) => {
        if (this.selectedBookingIds.has(b.id)) {
          b.isStatusSpinning = true;
        }
      });
    });

    const selectedBookings = this.allBookings
      .filter((b) => this.selectedBookingIds.has(b.id))
      .map((b) => {
        b.status = targetStatus;
        return b;
      });

    try {
      await this.eventsDomainStore.updateMultipleEventBookings(
        selectedBookings
      );
    } catch (e: any) {
      const errorMessage = this.getErrorMessage(e, "editBookingFailed");
      runInAction(() => {
        this.updateBookingError = errorMessage;
      });
      return;
    }

    if (targetStatus === EventBookingStatus.Cancelled) {
      this.updateRecordsBeforeReload();
    }

    await this.reloadEventDetailsAndBookings();
    this.clearSelectedBookings();
  };

  private getErrorMessage = (
    error: any,
    defaultErrorMessage: BookingUpdateError
  ): BookingUpdateError => {
    const errorDetail = (error as ProblemDetails).detail;
    switch (errorDetail) {
      case EventBookingErrorCodes.NotEnoughCapacity:
      case EventBookingErrorCodes.NotEnoughCapacityMultipleBookings:
        return "createBookingNotEnoughCapacity";
      case EventBookingErrorCodes.ConflictingRequest:
      case EventBookingErrorCodes.IncorrectParameterUsage:
        return "createBookingConfilictingRequest";
      case EventBookingErrorCodes.BookingAlreadyExists:
        return "createBookingAlreadyExists";
      default:
        return defaultErrorMessage;
    }
  };

  private updateRecordsBeforeReload = () => {
    runInAction(() => {
      this.confirmedEventBookings
        .filter((b) => this.selectedBookingIds.has(b.id))
        .forEach((b) =>
          this.eventsDomainStore.editableEvent.bookingSpacesDetails.onBookingRemoved(
            b.source
          )
        );

      this.confirmedEventBookings = this.confirmedEventBookings.filter(
        (b) => !this.selectedBookingIds.has(b.id)
      );
      this.waitlistEventBookings = this.waitlistEventBookings.filter(
        (b) => !this.selectedBookingIds.has(b.id)
      );
    });
  };

  isCurrentEventReleased = (): boolean => {
    const startDate =
      this.eventsDomainStore.editableEvent?.eventDateDetails?.startDate?.date;
    return (
      !!startDate &&
      // The calendar schedule defines the next **unreleased** month. Anything before this time is considered released
      isDateOneBeforeDateTwo(startDate, this.calendarSchedule?.startDate)
    );
  };

  /**
   * Commit any pending booking changes, without reloading bookings afterwards
   * @returns Were all pending changes cleared
   */
  private clearPendingChanges = async () => {
    if (this.hasUnsavedBookings) {
      this.processPendingBookingsDebounce.cancel();
      return await this.processPendingBookings(false);
    }
    return true;
  };

  getTimeZone = (): string => {
    return this.eventsDomainStore.getTimeZone();
  };

  canLoadEventBookings = () => {
    return !this.isEditingEventSeries && !this.isViewingEventSeries();
  };

  isViewingEventSeries = (): boolean => {
    return (
      this.eventsDomainStore.editableEvent.isRecurringEvent &&
      !this.isEditingEventSeries &&
      !this.eventsDomainStore.occurrenceDate
    );
  };

  canPublish(): boolean {
    const hasNonCosmeticChanges =
      this.eventsDomainStore.editableEvent.hasNonCosmeticChanges;

    // Non-cosmetic changes can only be made to event series that do not end in the current release period.
    const canMakeNonCosmeticChanges =
      this.eventsDomainStore.canMakeNonCosmeticChanges();

    if (
      this.isEditingEventSeries &&
      hasNonCosmeticChanges &&
      !canMakeNonCosmeticChanges
    ) {
      return false;
    }

    return this.eventsDomainStore.editableEvent.canPublish;
  }

  loadEventCategories = (): Promise<void> =>
    this.eventsDomainStore.getEventCategories(this.selectedCalendarType);

  get calendarColor(): Color | undefined {
    const calendar = this.eventsDomainStore.getCalendarByName(
      this.selectedCalendarName
    );
    if (calendar) {
      return Color[calendar.calendarColor as keyof typeof Color];
    }
    return undefined;
  }

  get calendarIcon(): IconSource {
    return getEventCalendarTypeDisplay(this.calendarTypeBySelectedCalendarName)
      .icon;
  }

  get isEnhancedCalendar(): boolean {
    return hasEnhancedFeatures(this.calendarTypeBySelectedCalendarName);
  }

  get calendarTypeBySelectedCalendarName(): EventCalendarType {
    // Prefer the calendarType persisted on the CMS village calendar
    // (edited via the Village Calendars admin page). Fall back to the
    // Integration API VillageCalendar, then to the legacy name-based
    // heuristic for older calendars that have no persisted calendarType.
    const selectedVillageId = this.selectableVillages.selectedItem?.value;

    const cmsMatch = this.villageCalendarsDomainStore?.villageCalendars.find(
      (c) =>
        (!selectedVillageId ||
          String(c.villageId) === String(selectedVillageId)) &&
        c.name === this.selectedCalendarName
    );
    const cmsType = toEventCalendarType(cmsMatch?.calendarType);
    if (cmsType) return cmsType;

    const integrationCalendar = this.eventsDomainStore.getCalendarByName(
      this.selectedCalendarName
    );
    const integrationType = toEventCalendarType(
      integrationCalendar?.calendarType
    );
    if (integrationType) return integrationType;

    return this.selectedCalendarName.includes(independentResidents)
      ? EventCalendarType.Enhanced
      : EventCalendarType.Engage;
  }

  get personTypeIdBySelectedCalendarName(): string | undefined {
    const calendar = this.eventsDomainStore.getCalendarByName(
      this.selectedCalendarName
    );
    if (calendar) {
      return calendar.personType;
    }
    return undefined;
  }

  /**
   * See {@link IEventsUiStore.personTypeIdsBySelectedCalendarName}.
   *
   * CMS Event API first (via the admin BFF `/api/villagecalendars` merge),
   * then the calendar's own `personTypeIds` (e.g. synthetic Multi-Village
   * calendars that have no real `villageId` to match against the CMS
   * store), then the Integration API `VillageCalendar.personType` as a
   * final fallback.
   */
  get personTypeIdsBySelectedCalendarName(): string[] {
    const calendar = this.eventsDomainStore.getCalendarByName(
      this.selectedCalendarName
    );
    const selectedVillageId = this.selectableVillages.selectedItem?.value;

    // CMS-first: look for a matching entry in the villageCalendars store,
    // which the admin BFF populates from internal/event/v1/calendars.
    if (this.villageCalendarsDomainStore && selectedVillageId) {
      const cmsMatch = this.villageCalendarsDomainStore.villageCalendars.find(
        (c) =>
          String(c.villageId) === String(selectedVillageId) &&
          c.name === this.selectedCalendarName
      );
      const cmsIds = cmsMatch?.personTypeIds?.filter(
        (id): id is string => !!id
      );
      if (cmsIds && cmsIds.length > 0) {
        return cmsIds;
      }
    }

    // Next: personTypeIds declared directly on the calendar object itself
    // (e.g. the synthetic Multi-Village calendars, which have no real
    // villageId to match against the CMS store above).
    const ownIds = calendar?.personTypeIds?.filter(
      (id): id is string => !!id
    );
    if (ownIds && ownIds.length > 0) {
      return ownIds;
    }

    // Fallback: the Integration API VillageCalendar carries a single value.
    if (calendar?.personType) {
      return [calendar.personType];
    }
    return [];
  }

  private loadCalendarEventsForCurrentView(
    startDate: Date,
    endDate: Date
  ): Promise<void> {
    // The categorised events endpoint rejects Multi-Village.
    return this.currentCalendarView === calendarViewCategory &&
      this.isSelectedVillageNotMultiVillage()
      ? this.loadCategorisedEvents(startDate, endDate)
      : this.eventsDomainStore.getAllEvents(
          startDate,
          endDate,
          this.selectedCalendarName
        );
  }

  loadCategorisedEvents(startDate: Date, endDate: Date): Promise<void> {
    return this.eventsDomainStore.loadCategorisedEvents(
      this.calendarTypeBySelectedCalendarName,
      zonedTimeToUtc(startDate, this.getTimeZone()),
      zonedTimeToUtc(endDate, this.getTimeZone()),
      this.selectableVillages.selectedItem?.value!,
      this.personTypeIdBySelectedCalendarName!
    );
  }

  setSelectedOccurrenceDate = (occurrenceDate: Date | null) => {
    this.selectedOccurrenceDate = occurrenceDate;
  };
}
